import { useUserStore } from '../stores/userStore';
import { useWorkOrdersStore } from '../stores/workOrdersStore';
import {
  emergencyClearAllData,
  IndexedDbCleanupService
} from './indexedDbCleanup';
import { auth } from './auth';

export class SessionManager {
  private static readonly SESSION_KEY = 'app-session';
  private static readonly LOG_PREFIX = '[Session Manager]';

  static async initializeSession(userName: string) {
    try {
      const sessionData = {
        userName,
        startTime: new Date().toISOString(),
        lastActivity: new Date().toISOString()
      };

      localStorage.setItem(this.SESSION_KEY, JSON.stringify(sessionData));

      await this.cleanupPreviousUserData(userName);

      return { success: true };
    } catch (error) {
      console.error(`${this.LOG_PREFIX} Failed to initialize session:`, error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Unknown error'
      };
    }
  }

  static async terminateSession(userName?: string) {
    try {
      await IndexedDbCleanupService.clearUserSpecificData(userName || '');

      localStorage.removeItem(this.SESSION_KEY);

      const userStore = useUserStore.getState();
      userStore.clearUser();

      const workOrdersStore = useWorkOrdersStore.getState();
      workOrdersStore.clearWorkOrders();

      return { success: true };
    } catch (error) {
      console.error(`${this.LOG_PREFIX} Failed to terminate session:`, error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Unknown error'
      };
    }
  }

  private static async cleanupPreviousUserData(currentUserName: string) {
    try {
      const storedSession = localStorage.getItem(this.SESSION_KEY);

      if (storedSession) {
        const sessionData = JSON.parse(storedSession);
        const previousUser = sessionData.userName;

        if (previousUser && previousUser !== currentUserName) {
          await IndexedDbCleanupService.clearAllCachedTablesForUser(
            previousUser
          );
        }
      }
    } catch (error) {
      console.error(error);
    }
  }

  static getCurrentSession() {
    try {
      const sessionData = localStorage.getItem(this.SESSION_KEY);
      return sessionData ? JSON.parse(sessionData) : null;
    } catch (error) {
      console.error(`${this.LOG_PREFIX} Failed to get current session:`, error);
      return null;
    }
  }

  static updateLastActivity() {
    try {
      const sessionData = this.getCurrentSession();
      if (sessionData) {
        sessionData.lastActivity = new Date().toISOString();
        localStorage.setItem(this.SESSION_KEY, JSON.stringify(sessionData));
      }
    } catch (error) {
      console.error(
        `${this.LOG_PREFIX} Failed to update last activity:`,
        error
      );
    }
  }

  static async handleSessionExpiration() {
    try {
      const currentSession = this.getCurrentSession();
      const userName = currentSession?.userName;

      await this.terminateSession(userName);

      return { success: true };
    } catch (error) {
      console.error(
        `${this.LOG_PREFIX} Failed to handle session expiration:`,
        error
      );

      try {
        await emergencyClearAllData();
      } catch (emergencyError) {
        console.error(
          `${this.LOG_PREFIX} Emergency cleanup failed:`,
          emergencyError
        );
      }

      return {
        success: false,
        error: error instanceof Error ? error.message : 'Unknown error'
      };
    }
  }

  static async handleUserSwitch(newUserName: string) {
    try {
      const currentSession = this.getCurrentSession();
      const previousUser = currentSession?.userName;

      if (previousUser && previousUser !== newUserName) {
        await IndexedDbCleanupService.clearUserSpecificData(previousUser);

        const workOrdersStore = useWorkOrdersStore.getState();
        workOrdersStore.clearWorkOrders();
      }

      await this.initializeSession(newUserName);

      return { success: true };
    } catch (error) {
      console.error(`${this.LOG_PREFIX} Failed to handle user switch:`, error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Unknown error'
      };
    }
  }

  static isSessionValid(): boolean {
    try {
      const sessionData = this.getCurrentSession();
      const isTokenValid = auth.isAuthenticated();
      const isUserStoreValid = useUserStore.getState().isAuthenticated;

      return !!(sessionData && isTokenValid && isUserStoreValid);
    } catch (error) {
      console.error(`${this.LOG_PREFIX} Failed to validate session:`, error);
      return false;
    }
  }

  static getSessionStats() {
    try {
      const sessionData = this.getCurrentSession();
      if (!sessionData) {
        return { hasSession: false };
      }

      const startTime = new Date(sessionData.startTime);
      const lastActivity = new Date(sessionData.lastActivity);
      const now = new Date();

      return {
        hasSession: true,
        userName: sessionData.userName,
        duration: now.getTime() - startTime.getTime(),
        timeSinceLastActivity: now.getTime() - lastActivity.getTime(),
        startTime: sessionData.startTime,
        lastActivity: sessionData.lastActivity
      };
    } catch (error) {
      console.error(`${this.LOG_PREFIX} Failed to get session stats:`, error);
      return {
        hasSession: false,
        error: error instanceof Error ? error.message : 'Unknown error'
      };
    }
  }
}
